/**
 * F19c — Self-decrypt canary
 *
 * Runs at device-init time to verify that our local crypto state can perform
 * a synthetic self-encrypt → self-decrypt round-trip. If this fails, our
 * IndexedDB / SubtleCrypto / key material is in an unusable state, and every
 * incoming X3DH receive will fail with "aes/gcm: invalid ghash tag". We want
 * to surface that within one init round, not 5 hours of failed decrypts.
 *
 * This function NEVER throws — the canary's job is to report, not propagate.
 */

import { selfEncrypt, selfDecrypt } from "../hooks/useCrypto-self";

export async function runSelfCanary(
  identityPrivateKey: Uint8Array,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const rand = Math.random().toString(36).slice(2, 10);
    const plaintext = `canary-${Date.now()}-${rand}`;

    const ciphertext = await selfEncrypt(plaintext, identityPrivateKey);
    const decrypted = await selfDecrypt(ciphertext, identityPrivateKey);

    if (decrypted !== plaintext) {
      return { ok: false, error: "mismatch" };
    }
    return { ok: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, error: `throw: ${msg}` };
  }
}
